import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card, DifficultyBadge, useCategoryLabel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Check, CheckCircle2, XCircle, AlertTriangle, FileText, ChevronRight, Info } from "lucide-react";

const WIZARD_STEPS = [
  { key: `SYMPTOMS`, label: `1. Classification`, desc: `Incident Root Mapping` },
  { key: `PRESCREEN`, label: `2. Pre-Screening`, desc: `Reporter Role & Delays` },
  { key: `DEMOGRAPHICS`, label: `3. Demographics`, desc: `Victim & Identity Details` },
  { key: `FINANCIAL`, label: `4. Financial Ledger`, desc: `Double-Legged Audit` },
  { key: `BRANCH_FLOW`, label: `5. Incident Details`, desc: `Specific Branch Trees` },
  { key: `SUMMARY`, label: `6. Review & Consent`, desc: `IOC & DPDP Verification` },
];

export default function NCRPForm() {
  const { t, lang } = useLang();
  const catLabel = useCategoryLabel();
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);
  const [values, setValues] = useState({});
  const [result, setResult] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => { api.get("/form-scenarios").then((r) => setScenarios(r.data)); }, []);

  const start = (s) => { setActive(s); setValues({}); setResult(null); setActiveStep(0); };
  const setVal = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const getStepForField = (key) => {
    if (key === "category") return 0;
    if (key === "delay") return 1;
    if (key === "gender" || key === "id_type" || key === "ps") return 2;
    if (["amount", "account_number", "ifsc", "txn_id"].includes(key)) return 3;
    if (key === "incident_date") return 4;
    return 4; // default to incident details
  };

  const validate = async () => {
    const diffs = active.fields.map((f) => {
      const entered = (values[f.key] || "").toString().trim();
      const ok = entered.toLowerCase() === f.correct.toString().toLowerCase();
      return { key: f.key, label: lang === "en" ? f.label_en : f.label_hi, entered, correct: f.correct, ok, hint: lang === "en" ? f.hint_en : f.hint_hi };
    });
    const correctCount = diffs.filter((d) => d.ok).length;
    const mistakes = diffs.filter((d) => !d.ok).map((d) => `${d.label}: expected ${d.correct}`);
    setResult({ diffs, correctCount, total: active.fields.length });
    
    await api.post("/sessions", {
      module: "ncrp_form", scenario_id: active.scenario_id, category_code: active.category_code,
      score: correctCount, max_score: active.fields.length, mistakes, details: values,
    });
    toast[correctCount === active.fields.length ? "success" : "error"](
      correctCount === active.fields.length ? t("allCorrect") : `${correctCount}/${active.fields.length} ${t("correct")}`
    );
  };

  if (active) {
    const stepDef = WIZARD_STEPS[activeStep];
    const fieldsForStep = active.fields.filter(f => getStepForField(f.key) === activeStep);

    return (
      <div className="flex h-[90vh] -mx-4 -mt-4 bg-slate-50 font-sans overflow-hidden border-t-4 border-red-600">
        
        {/* Left Sidebar */}
        <div className="w-[280px] bg-[#111827] text-white flex flex-col shrink-0">
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="bg-amber-500 text-slate-900 font-bold h-10 w-10 rounded text-lg flex items-center justify-center shrink-0">NC</div>
              <div>
                <h1 className="font-bold text-[15px] leading-tight tracking-wide text-white">NCRP PORTAL</h1>
                <div className="text-[10px] text-orange-400 font-semibold tracking-widest uppercase">MHA CYBER INTAKE</div>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {WIZARD_STEPS.map((step, idx) => {
                const isActive = idx === activeStep;
                const isCompleted = idx < activeStep;
                return (
                  <div key={step.key} className="flex gap-4 relative">
                    {/* Connecting Line */}
                    {idx !== WIZARD_STEPS.length - 1 && (
                      <div className={`absolute left-3 top-8 bottom-[-24px] w-[2px] ${isCompleted ? 'bg-emerald-500' : 'bg-slate-800'}`} />
                    )}
                    
                    {/* Circle Indicator */}
                    <div className={`relative z-10 shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      isCompleted ? 'bg-emerald-500 text-white' :
                      isActive ? 'bg-amber-500 text-slate-900' :
                      'bg-slate-800 text-slate-500'
                    }`}>
                      {isCompleted ? <Check className="h-4 w-4" /> : idx + 1}
                    </div>
                    
                    {/* Step Text */}
                    <div className="pt-0.5">
                      <div className={`text-sm font-bold ${
                        isCompleted ? 'text-emerald-400' :
                        isActive ? 'text-amber-500' :
                        'text-slate-400'
                      }`}>
                        {step.label}
                      </div>
                      <div className="text-[11px] text-slate-500">{step.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sidebar Summary Card */}
            <div className="mt-10 bg-slate-50 rounded-lg p-4 text-slate-900">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Classification</div>
              <div className="text-sm font-semibold text-amber-600 mb-3">{catLabel(active.category_code)}</div>
              
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Loss Reported</div>
              <div className="text-sm font-bold text-emerald-600 mb-3">₹ {values.amount || "0"}</div>

              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Scenario Active</div>
              <div className="text-xs font-medium text-slate-700">{lang === "en" ? active.title_en : active.title_hi}</div>
            </div>
          </div>
          
          <div className="p-4 text-[10px] text-slate-500 border-t border-slate-800">
            Govt of India Security Standard
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 bg-[#f8fafc] relative">
          
          {/* Top Warning Strip */}
          <div className="bg-[#1e293b] text-white px-4 py-2 flex items-center text-xs font-medium shrink-0">
            <span className="bg-red-600 px-2 py-0.5 rounded text-white font-bold mr-3 tracking-wider">HELPLINE 1930</span>
            <span className="opacity-90">For reporting online financial fraud, call the National Helpline number 1930 immediately. Cybercrimes must be reported within 24 hours.</span>
          </div>
          
          {/* Scrollable Form Content */}
          <div className="flex-1 overflow-y-auto p-8 lg:p-12">
            <div className="max-w-3xl">
              
              <h2 className="text-3xl font-bold text-[#1e3a8a] mb-2">{stepDef.label.replace(/^[0-9.]+\s/, '')}</h2>
              <p className="text-slate-600 text-[15px] mb-8 font-medium">Choose the context of the report to initialize the correct legal intake path.</p>

              {/* Context Banner */}
              <div className="mb-8 bg-white border-l-4 border-blue-600 shadow-sm rounded-r-lg p-5">
                <div className="flex items-center gap-2 text-blue-800 font-bold text-xs tracking-wider uppercase mb-2">
                  <Info className="h-4 w-4" /> Scenario Context
                </div>
                <p className="text-slate-700 text-sm mb-3">{lang === "en" ? active.narrative_en : active.narrative_hi}</p>
                <div className="bg-amber-50 text-amber-900 px-3 py-2 rounded text-xs font-medium border border-amber-200">
                  <AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                  Warning: {lang === "en" ? active.common_mistake_en : active.common_mistake_hi}
                </div>
              </div>

              {/* Form Fields Card */}
              <div className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 lg:p-8">
                {fieldsForStep.length === 0 && activeStep < 5 && (
                  <div className="text-sm text-slate-500 italic text-center py-4">No fields required for this step. Please continue.</div>
                )}

                {activeStep === 5 && (
                  <div>
                    <h3 className="font-bold text-slate-900 mb-4 text-lg">Declaration & Undertaking</h3>
                    
                    <div className="space-y-3 mb-8">
                      {active.fields.map(f => (
                         <div key={f.key} className="flex justify-between py-2 border-b border-slate-100 text-sm">
                           <span className="text-slate-500 font-medium">{lang === "en" ? f.label_en : f.label_hi}</span>
                           <span className="text-slate-900 font-semibold">{values[f.key] || "—"}</span>
                         </div>
                      ))}
                    </div>

                    {!result ? (
                       <Button onClick={validate} className="w-full bg-[#1e3a8a] hover:bg-blue-900 text-white py-6 text-base font-bold">
                         I ACCEPT & PROCEED ✓
                       </Button>
                    ) : (
                      <div className="mt-4 p-6 rounded-lg bg-slate-50 border border-slate-200 text-center">
                        <div className="text-4xl font-black text-[#1e3a8a] mb-2">{result.correctCount}/{result.total}</div>
                        <div className="text-slate-600 font-medium mb-4">Simulation Score</div>
                        <Button variant="outline" onClick={() => start(active)} className="w-full font-bold">Reset Scenario</Button>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-6">
                  {/* Standard Demographics Fields (Always shown for realism) */}
                  {activeStep === 2 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-slate-100 mb-6">
                      <div>
                        <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2 block">Full Name <span className="text-red-500">*</span></Label>
                        <Input value={values["full_name"] || ""} onChange={(e) => setVal("full_name", e.target.value)} disabled={!!result} placeholder="Enter full name" className="h-11 border-slate-300 focus:ring-2 focus:ring-[#1e3a8a] text-[15px] bg-slate-50" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2 block">Mobile Number <span className="text-red-500">*</span></Label>
                        <Input value={values["mobile_number"] || ""} onChange={(e) => setVal("mobile_number", e.target.value)} disabled={!!result} placeholder="Enter mobile number" className="h-11 border-slate-300 focus:ring-2 focus:ring-[#1e3a8a] text-[15px] bg-slate-50" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2 block">State <span className="text-red-500">*</span></Label>
                        <Select value={values["state"] || ""} onValueChange={(v) => setVal("state", v)} disabled={!!result}>
                          <SelectTrigger className="h-11 border-slate-300 focus:ring-2 focus:ring-[#1e3a8a] text-[15px] bg-slate-50"><SelectValue placeholder="Select State" /></SelectTrigger>
                          <SelectContent>
                            {[
                              "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", 
                              "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", 
                              "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", 
                              "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", 
                              "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", 
                              "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", 
                              "Uttarakhand", "West Bengal"
                            ].map(state => (
                              <SelectItem key={state} value={state}>{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2 block">Pincode <span className="text-red-500">*</span></Label>
                        <Input value={values["pincode"] || ""} onChange={(e) => setVal("pincode", e.target.value)} disabled={!!result} placeholder="Enter pincode" className="h-11 border-slate-300 focus:ring-2 focus:ring-[#1e3a8a] text-[15px] bg-slate-50" />
                      </div>
                    </div>
                  )}

                  {fieldsForStep.map((f) => {
                    const d = result?.diffs.find((x) => x.key === f.key);
                    return (
                      <div key={f.key}>
                        <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2 block">
                          {lang === "en" ? f.label_en : f.label_hi} {f.required && <span className="text-red-500">*</span>}
                        </Label>
                        
                        {f.type === "select" ? (
                          <Select value={values[f.key] || ""} onValueChange={(v) => setVal(f.key, v)} disabled={!!result}>
                            <SelectTrigger className="h-11 border-slate-300 focus:ring-2 focus:ring-[#1e3a8a] text-[15px] bg-slate-50">
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                            value={values[f.key] || ""} onChange={(e) => setVal(f.key, e.target.value)}
                            disabled={!!result} 
                            className="h-11 border-slate-300 focus:ring-2 focus:ring-[#1e3a8a] text-[15px] bg-slate-50"
                          />
                        )}
                        
                        {d && (
                          <div className={`mt-3 text-sm p-3 rounded flex items-start gap-2 ${d.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
                            {d.ok ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <XCircle className="h-5 w-5 shrink-0" />}
                            <div>
                              <div className="font-bold">{d.ok ? "Correct" : "Incorrect"}</div>
                              {!d.ok && <div className="mt-1 text-xs">Expected: <span className="font-mono bg-white px-1 py-0.5 rounded border border-red-100">{d.correct}</span><br/><span className="text-red-600 mt-1 block">{d.hint}</span></div>}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Fixed Navigation */}
          <div className="bg-white border-t border-slate-200 p-4 px-8 flex justify-between items-center shrink-0">
            <Button variant="outline" onClick={() => { setActiveStep(p => Math.max(0, p - 1)); setActive(null); }} className="font-semibold text-slate-700" disabled={activeStep === 0}>
              ← BACK
            </Button>
            {activeStep < 5 && (
              <Button onClick={() => setActiveStep(p => Math.min(5, p + 1))} className="bg-[#1e3a8a] hover:bg-blue-900 text-white font-bold px-8">
                CONTINUE →
              </Button>
            )}
          </div>

        </div>
      </div>
    );
  }

  // Scenario Selection Screen (matches the style of the dashboard)
  return (
    <div className="max-w-6xl mx-auto pb-12">
      <div className="mb-8 bg-white p-6 rounded-lg border border-slate-200 shadow-sm flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a8a] mb-1">NCRP Simulator Scenarios</h1>
          <p className="text-slate-500 text-sm">Select a live case study to begin intake training.</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {scenarios.map((s) => (
          <Card key={s.scenario_id} className="border-slate-200 shadow-sm hover:border-[#1e3a8a] hover:shadow-md transition-all flex flex-col group bg-white overflow-hidden cursor-pointer" onClick={() => start(s)}>
            <div className="h-2 w-full bg-[#1e3a8a]" />
            <div className="p-6 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-1 rounded">{catLabel(s.category_code)}</span>
                <DifficultyBadge level={s.difficulty} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#1e3a8a]">{lang === "en" ? s.title_en : s.title_hi}</h3>
              <p className="text-sm text-slate-500 line-clamp-3 mb-6 flex-1">{lang === "en" ? s.narrative_en : s.narrative_hi}</p>
              <div className="flex items-center text-sm font-bold text-[#1e3a8a]">
                INITIALIZE INTAKE <ChevronRight className="h-4 w-4 ml-1" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
